/**
 * CommandApi — methods that MUTATE data on the server.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  PUT IT HERE if the request CREATES, UPDATES, or DELETES data.    │
 * │                                                                    │
 * │  This is about DATA MUTATION, not HTTP method:                     │
 * │    POST /ingest               → here (creates blocks)           │
 * │    POST /assistant            → here (creates session + blocks) │
 * │                                                                    │
 * │  For reads (list, search, details), use query-api instead.        │
 * └─────────────────────────────────────────────────────────────────────┘
 */
import { apiDelete, apiPatch, apiPost, apiPostFormData, getAuthHeaders, } from "./base-query-api";
import { API_BASE_URL } from "@/viewer-config";
export async function sendIdentifierOtp(type, value) {
    const payload = type === "phone" ? { type, phone: value } : { type, email: value };
    const res = await apiPost("/account/identifier/send-otp", payload);
    if (!res.success)
        throw new Error(res.error);
}
export async function confirmIdentifierOtp(type, value, token) {
    const payload = type === "phone"
        ? { type, phone: value, token }
        : { type, email: value, token };
    const res = await apiPost("/account/identifier/confirm-otp", payload);
    if (!res.success || !res.data)
        throw new Error(res.error ?? "Failed to confirm");
    return res.data;
}
export async function ingestText(options) {
    const { collectionId, clientBlockId, doc, ...rest } = options;
    const payload = {
        ...rest,
        ...(collectionId != null ? { collectionId } : {}),
        ...(clientBlockId != null ? { clientBlockId } : {}),
        ...(doc != null ? { doc } : {}),
    };
    const res = await apiPost("/ingest", payload);
    const resBody = res;
    if (!resBody.accepted && !res.success) {
        throw new Error(res.error ?? "Failed to ingest");
    }
}
export async function ingestWithFiles(formData) {
    const res = await apiPostFormData("/ingest", formData);
    const body = res;
    if (!body.accepted && !res.success) {
        throw new Error(res.error ?? "Failed to ingest");
    }
}
export async function sendAssistantMessage(options) {
    const res = await apiPost("/assistant", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to send assistant message");
    }
    return res.data;
}
export async function sendAssistantWithFiles(formData) {
    const res = await apiPostFormData("/assistant", formData);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to send assistant message");
    }
    return res.data;
}
export async function createReminder(input) {
    const res = await apiPost("/reminder/create", input);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to create reminder");
    }
}
// ---------------------------------------------------------------------------
// Block Actions
// ---------------------------------------------------------------------------
export async function surfaceBlock(blockId) {
    const res = await apiPost(`/block/${blockId}/surface`, {});
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to surface block");
    }
    return res.data;
}
export async function deleteBlock(blockId) {
    const res = await apiPost(`/block/${blockId}/delete`, {});
    if (!res.success) {
        throw new Error(res.error ?? "Failed to delete block");
    }
}
/**
 * Thrown by `updateBlock` when the server rejects the PATCH with
 * `LOCK_NOT_HELD` (HTTP 409): the caller's edit lock was taken over
 * or expired between their last heartbeat and the save. Distinct
 * from a generic network/server error so the edit shell can route
 * it into the lock state machine's `lost` branch (which surfaces
 * the draft-preservation banner) instead of the "Failed to save"
 * toolbar message.
 */
export class LockLostError extends Error {
    constructor() {
        super("Lock no longer held");
        this.name = "LockLostError";
    }
}
/**
 * Update a text block's title and/or content. The caller MUST currently
 * hold the edit lock — claim via `claimBlockLock` first. Throws
 * `LockLostError` on 409 `LOCK_NOT_HELD` (someone took over the lock
 * while the user was typing) and a generic Error on the usual non-2xx
 * server failures.
 *
 * Uses a direct fetch (rather than `apiPatch`) so the 409 branch can
 * be distinguished from other non-2xx responses without parsing the
 * thrown error message. Mirrors the same pattern as `claimBlockLock`
 * and `heartbeatBlockLock`.
 */
// eslint-disable-next-line putty/no-direct-fetch
export async function updateBlock(blockId, patch) {
    const url = `${API_BASE_URL}/v1/block/${blockId}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(patch),
        credentials: "include",
    });
    if (res.status === 409) {
        throw new LockLostError();
    }
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const body = (await res.json());
    if (!body.success || !body.data) {
        throw new Error(body.error ?? "Failed to update block");
    }
    return body.data.block;
}
/**
 * Claim the edit lock on a block.
 *
 * `force: true` overrides an existing live lock (used by the
 * "Edit anyway" takeover flow). Default `false` returns a
 * `held-by-other` result with the current owner so the UI can prompt.
 *
 * Uses a custom fetch (rather than `apiPost`) because the 409 carries
 * a `lock` payload we want to surface back to the caller; the base
 * client's "throw on !ok" path would swallow it.
 */
export async function claimBlockLock(blockId, options = {}) {
    const url = new URL(`${API_BASE_URL}/v1/block/${blockId}/lock`);
    if (options.force === true)
        url.searchParams.set("force", "true");
    const authHeaders = await getAuthHeaders();
    let res;
    try {
        res = await fetch(url.toString(), {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: "{}",
            credentials: "include",
        });
    }
    catch (e) {
        return { ok: false, reason: "error", message: e instanceof Error ? e.message : "Network error" };
    }
    const text = await res.text();
    let body;
    try {
        body = text.length > 0 ? JSON.parse(text) : {};
    }
    catch {
        body = {};
    }
    if (res.status === 409) {
        const lock = body.lock;
        if (lock)
            return { ok: false, reason: "held-by-other", existing: lock };
        return { ok: false, reason: "error", message: "Lock conflict" };
    }
    if (res.status === 403) {
        const message = body.message ?? "No write access to this block";
        return { ok: false, reason: "forbidden", message };
    }
    if (!res.ok) {
        const message = body.message ??
            body.error ??
            `HTTP ${res.status}`;
        return { ok: false, reason: "error", message };
    }
    const data = body.data;
    if (!data?.lock) {
        return { ok: false, reason: "error", message: "Malformed lock response" };
    }
    return { ok: true, lock: data.lock };
}
/**
 * Extend the caller's lock TTL. The client calls this on a 30-second
 * interval while edit mode is active. A "lost" result means the
 * editor should bail out — someone else now holds the lock.
 */
export async function heartbeatBlockLock(blockId) {
    const url = `${API_BASE_URL}/v1/block/${blockId}/lock/heartbeat`;
    const authHeaders = await getAuthHeaders();
    let res;
    try {
        res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: "{}",
            credentials: "include",
        });
    }
    catch (e) {
        return { ok: false, reason: "error", message: e instanceof Error ? e.message : "Network error" };
    }
    if (res.status === 409)
        return { ok: false, reason: "lost" };
    if (!res.ok) {
        return { ok: false, reason: "error", message: `HTTP ${res.status}` };
    }
    const body = (await res.json().catch(() => ({})));
    if (!body.data?.expiresAt) {
        return { ok: false, reason: "error", message: "Malformed heartbeat response" };
    }
    return { ok: true, expiresAt: body.data.expiresAt };
}
/**
 * Release the caller's lock. Idempotent and stale-tab-safe — see the
 * server-side `BlockEditLockEntity.release`. Best-effort: failures
 * are swallowed because the 90-second server-side TTL is the ultimate
 * backstop. The lock guarantee is "at most one editor at a time
 * within the TTL window" — a release call that fails just means
 * other users wait one TTL window before being able to take over.
 */
export async function releaseBlockLock(blockId) {
    try {
        await apiDelete(`/block/${blockId}/lock`);
    }
    catch {
        // No-op — see the docstring's rationale on TTL as backstop.
    }
}
// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------
/**
 * @deprecated Use `setPins({type: "account"}, "feed", items)` instead. The
 * `/v1/account/preferences` endpoint is a back-compat proxy onto the new
 * Pin model and will be removed once all clients migrate.
 */
export async function saveAccountHomepagePins(pins) {
    const res = await apiPost("/account/preferences", { pins });
    if (!res.success) {
        throw new Error(res.error ?? "Failed to save homepage pins");
    }
}
/**
 * Toggle a single item on/off in a (scope, surface). Returns the new
 * boolean state plus the post-toggle pin list in position order.
 *
 * Rejects with HTTP 409 (ConflictError) when pinning would exceed
 * MAX_PINS_PER_SURFACE (5).
 */
export async function togglePin(scope, surface, item) {
    const body = { scope, surface, item };
    const res = await apiPost("/pin/toggle", body);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to toggle pin");
    }
    return res.data;
}
/**
 * Replace the entire pin list for a (scope, surface) slot. Used by the
 * bootstrap path and any drag-reorder flow that rewrites positions
 * wholesale.
 */
export async function setPins(scope, surface, items) {
    const res = await apiPost("/pin/set", {
        scope,
        surface,
        items,
    });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to save pins");
    }
    return res.data.pins;
}
/**
 * Claim a username for the authenticated account. The username is the
 * caller's handle in the global root namespace (powers `putty.me/{name}`).
 *
 * Throws on:
 *   - Format violations (length, character class, reserved name)
 *   - Conflict (name already taken by another user OR by an existing group slug)
 *   - Account already has a username (current API is single-shot; rename
 *     flow is a follow-on)
 */
export async function claimUsername(username) {
    const res = await apiPost("/account/me/username", { username });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to claim username");
    }
    return res.data.account;
}
export async function saveAccountPreferencesMap(patch) {
    const res = await apiPost("/account/preferences/map", {
        preferences: patch,
    });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to save preferences");
    }
    return res.data.preferences;
}
export async function createCollection(options) {
    const res = await apiPost("/collection/create", options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to create collection");
    }
    return res.data;
}
/**
 * Convenience wrapper around `createCollection({ layerIndex: 0 })` for
 * the Groups switcher's inline create flow.
 */
export async function createGroup(options) {
    return createCollection({ ...options, layerIndex: 0 });
}
export async function createChildCollection(parentId, options) {
    const res = await apiPost(`/collection/${parentId}/create-child`, options);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to create child collection");
    }
    return res.data;
}
/**
 * Convenience wrapper around `createChildCollection` that always
 * creates a layer-2 Stack regardless of the parent's layer. Used by
 * the multiselect "Create stack" affordance and the drag-to-create-stack
 * gesture, both of which can now target Group parents (the homepage
 * Stream creates Stacks directly under the user's Primary Group) in
 * addition to layer-1 Collection parents. Server validates that the
 * parent's layer is in `LEGAL_PARENT_CHILD_LAYER_PAIRS` for a layer-2
 * child — currently Group (0) and Collection (1).
 */
export async function createStack(parentCollectionId, options) {
    return createChildCollection(parentCollectionId, {
        ...options,
        childLayerIndex: 2,
    });
}
/**
 * Move blocks along a parent-child edge in the Group/Collection/Stack
 * hierarchy, in either direction. Legal edges are Group↔Collection,
 * Group↔Stack, and Collection↔Stack. Server-side enforces the
 * parent-child guardrail, the legal-pair restriction, and write access
 * on both ends.
 */
export async function moveBlocksBetweenParentChild(blockIds, targetCollectionId) {
    const res = await apiPost("/block/move-between-parent-child", {
        blockIds,
        targetCollectionId,
    });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to move blocks between parent and child");
    }
    return res.data;
}
/**
 * Share a private Collection with a list of email addresses. The server
 * mints (or reuses) an implicit Group whose direct member set exactly
 * matches `{me, ...invitees}`, reparents the Collection under it, and
 * returns `{ group, warnings }`. The `warnings` array carries any
 * non-blocking notes the UI should surface (e.g. "you were removed as
 * direct owner; access is now inherited from <group>").
 *
 * Replaces the removed `addMemberToCollection` for collection-level
 * sharing flows.
 */
export async function shareCollection(collectionId, inviteeEmails, role = "editor") {
    const res = await apiPost(`/collection/${collectionId}/share`, { inviteeEmails, role });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to share collection");
    }
    return { group: res.data.group, warnings: res.data.warnings ?? [] };
}
/**
 * Detach a private Collection from the caller's Primary, transitioning
 * it into the standalone state (`parentCollectionId === null`) and
 * seeding the caller's owner row. Used by the share-without-Group flow
 * — call this once before adding members directly to a Collection that
 * was previously sitting under the user's Primary.
 *
 * Idempotent: returns `alreadyDetached: true` if the Collection is
 * already standalone.
 */
export async function detachCollectionFromPrimary(collectionId) {
    const res = await apiPost(`/collection/${collectionId}/detach-from-primary`, {});
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to detach collection from primary");
    }
    return res.data;
}
/**
 * Add a member to a standalone Collection by email. Standalone
 * Collections use the same role schema as Groups (owner / editor /
 * viewer); the caller picks the role via the modal's role selector
 * and passes it through. For Group-rooted Collections, use
 * `addMemberToGroup` on the parent Group instead — the server rejects
 * Group-rooted targets here with a friendly redirect.
 */
export async function addMemberToCollection(collectionId, email, role = "editor") {
    const res = await apiPost(`/collection/${collectionId}/add-member`, { email, role });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to add member to collection");
    }
    return res.data;
}
/**
 * Remove a direct member from a standalone Collection.
 */
export async function removeMemberFromCollection(collectionId, accountId) {
    const res = await apiDelete(`/collection/${collectionId}/member/${accountId}`);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to remove member from collection");
    }
}
/**
 * Add a member to a Group by email. Direct membership lives on Groups
 * and on standalone Collections; for Collections, use
 * `addMemberToCollection` instead.
 *
 * The server enforces the iMessage-style 2-vs-3+ rules on implicit
 * Groups: addMember on a frozen 2-person pair is rejected.
 */
export async function addMemberToGroup(groupId, email, role = "editor") {
    const res = await apiPost(`/group/${groupId}/add-member`, {
        email,
        role,
    });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to add member to group");
    }
    return res.data;
}
/**
 * Remove a member from a Group. Server enforces the iMessage-style
 * lifecycle rules: 2-person frozen → forbidden; 3-person → forbidden
 * (would leave a pair); 4+ → allowed.
 */
export async function removeMemberFromGroup(groupId, accountId) {
    const res = await apiDelete(`/group/${groupId}/member/${accountId}`);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to remove member from group");
    }
}
export async function updateCollection(collectionId, patch) {
    const res = await apiPatch(`/collection/${collectionId}`, patch);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to update collection");
    }
    return res.data.collection;
}
/**
 * Claim, rename, or clear a collection's URL slug.
 *
 * Pass a string to set/rename, `undefined` to clear. The wire format
 * sends JSON `null` for the clear case (JSON has no `undefined`); the
 * server's schema converts back to `Optional<string>` at the entity
 * boundary, keeping the codebase's null-free convention intact.
 *
 * The slug shape is validated server-side (3–20 chars, lowercase,
 * hyphen-separated, no reserved root names for layer-0 Groups) so the
 * caller only needs to surface the friendly error message on failure.
 *
 * After this resolves, the collection's public URL becomes:
 *   - layer-0 Group   → `putty.me/{slug}`
 *   - standalone      → `putty.me/{ownerUsername}/{slug}`
 *   - group-rooted    → `putty.me/{parentGroupSlug}/{slug}`
 *
 * Clearing the slug releases the root-name reservation (for Groups)
 * and reverts the collection to the member-only `/g/{id}` URL.
 */
export async function setCollectionSlug(collectionId, slug) {
    const res = await apiPatch(`/collection/${collectionId}/slug`, { slug: slug ?? null });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to update collection URL");
    }
    return res.data.collection;
}
/**
 * Mark a Collection or Group as publicly readable.
 *
 * After this resolves, the collection (and every descendant) becomes
 * readable by anonymous viewers via `putty.me/{username}/{slug}` or
 * `putty.me/{groupslug}` — provided the row has a slug claimed. Writes
 * are unaffected; publication is a read-only escalation.
 *
 * Server enforces:
 *   - Caller must be the collection's owner.
 *   - Only layer-0 Groups and standalone (parentless) layer-1
 *     Collections are publishable. Primary / implicit / group-rooted
 *     children / Stacks return a friendly 4xx pointing the caller at
 *     the correct target.
 */
export async function publishCollection(collectionId) {
    const res = await apiPost(`/collection/${collectionId}/publish`, {});
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to publish collection");
    }
    return res.data.collection;
}
/**
 * Revert a published Collection or Group to members-only. Idempotent on
 * already-private rows. Server enforces owner role on the target.
 */
export async function unpublishCollection(collectionId) {
    const res = await apiPost(`/collection/${collectionId}/unpublish`, {});
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to unpublish collection");
    }
    return res.data.collection;
}
/**
 * Move a Collection under a new parent. Used by the multiselect "Add
 * to group" affordance to drop layer-1 Collections under layer-0
 * Groups.
 *
 * Server enforces owner-on-source AND editor-on-destination (since
 * 2026-04-30), the layer cascade, cycle prevention, and atomically
 * clears all direct CollectionMember rows on the moved subtree. The
 * `warnings` field reports any user-facing consequences of the clear
 * — typically "you were removed as direct owner" or "<N> other
 * members lost direct access".
 *
 * Detach-to-root (passing `undefined`) is no longer legal for non-Group
 * collections; the server returns a 4xx ValidationError. The wire
 * format keeps the `null` allowance for old clients to fail gracefully.
 */
export async function setCollectionParent(collectionId, parentCollectionId) {
    const res = await apiPatch(`/collection/${collectionId}/parent`, { parentCollectionId: parentCollectionId ?? null });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to move collection");
    }
    return { collection: res.data.collection, warnings: res.data.warnings ?? [] };
}
/**
 * Soft-delete a Collection (standalone, Group-rooted, or Stack). Layer-0
 * Groups go through `deleteGroup` on the `/v1/group/:id` endpoint
 * instead — those have a slightly different permission model.
 *
 * Auth (server-enforced): admin on the target OR editor on the parent.
 * Pins targeting the deleted collection are cascade-unpinned server-side
 * so dangling homepage / nav tiles disappear in the same write.
 */
export async function deleteCollection(collectionId) {
    const res = await apiDelete(`/collection/${collectionId}`);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to delete collection");
    }
}
export async function copyBlocksToCollection(collectionId, blockIds) {
    const res = await apiPost(`/collection/${collectionId}/copy-blocks`, {
        blockIds,
    });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to copy blocks to collection");
    }
    return res.data;
}
export async function createUserAnnotation(input) {
    const res = await apiPost("/user-annotation/create", input);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to create user annotation");
    }
    return res.data.userAnnotation;
}
export async function updateUserAnnotation(id, patch) {
    const res = await apiPatch(`/user-annotation/${id}`, patch);
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to update user annotation");
    }
    return res.data.userAnnotation;
}
export async function deleteUserAnnotation(id) {
    const res = await apiDelete(`/user-annotation/${id}`);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to delete user annotation");
    }
}
// ---------------------------------------------------------------------------
// Notification
// ---------------------------------------------------------------------------
/**
 * Advance the server-side "notifications viewed up to" cursor to now.
 * Fired by the notifications popup whenever it opens so the unread badge
 * resets and the new read state follows the user across devices on the
 * next list fetch. Idempotent — re-firing only bumps the timestamp.
 *
 * Returns void on success; throws when the request fails so the popup's
 * optimistic-update mutation can roll back the cache.
 */
export async function markNotificationsViewed() {
    const res = await apiPost("/notification/mark-viewed", {});
    if (!res.success) {
        throw new Error(res.error ?? "Failed to mark notifications viewed");
    }
}
